import { isSdk429Error, isSdkRateLimited, sdkRateLimitUntil } from './sdkRateLimit';

/** Max concurrent Sharetribe SDK requests — avoids ERR_INSUFFICIENT_RESOURCES. */
export const SDK_MAX_CONCURRENT_REQUESTS = 4;

let activeCount = 0;
const pendingQueue = [];
let globalRateLimitedUntil = 0;

export const getGlobalSdkRateLimitedUntil = () => globalRateLimitedUntil;

export const noteGlobalSdk429 = (fromMs = Date.now()) => {
  globalRateLimitedUntil = sdkRateLimitUntil(fromMs);
};

export const isGlobalSdkRateLimited = (now = Date.now()) =>
  isSdkRateLimited(globalRateLimitedUntil, now);

const waitForGlobalCooldown = () => {
  const waitMs = globalRateLimitedUntil - Date.now();
  if (waitMs <= 0) {
    return Promise.resolve();
  }
  return new Promise(resolve => setTimeout(resolve, waitMs));
};

const drainQueue = () => {
  while (activeCount < SDK_MAX_CONCURRENT_REQUESTS && pendingQueue.length > 0) {
    const next = pendingQueue.shift();
    next.run();
  }
};

const startTask = (resolve, reject, taskFn) => {
  activeCount += 1;
  waitForGlobalCooldown()
    .then(() => Promise.resolve().then(taskFn))
    .then(resolve)
    .catch(err => {
      if (isSdk429Error(err)) {
        noteGlobalSdk429();
      }
      reject(err);
    })
    .finally(() => {
      activeCount -= 1;
      drainQueue();
    });
};

/**
 * Run Sharetribe SDK work with bounded concurrency and a shared post-429 cooldown.
 * @param {() => Promise<*>} taskFn
 */
export const scheduleSdkRequest = taskFn => {
  return new Promise((resolve, reject) => {
    const job = () => startTask(resolve, reject, taskFn);

    if (activeCount < SDK_MAX_CONCURRENT_REQUESTS) {
      job();
    } else {
      pendingQueue.push({ run: job });
    }
  });
};

/** Test helper — reset module state between unit tests. */
export const resetSdkRequestQueueForTests = () => {
  activeCount = 0;
  pendingQueue.length = 0;
  globalRateLimitedUntil = 0;
};
